import type { Map as MapLibreMap } from "maplibre-gl";
import {
  BASEMAP_PREF_KEY,
  isOpenFreeMapBasemapLayer,
  pickSatelliteLayerBeforeId,
  readBasemapPref,
  SATELLITE_LAYER_ID,
  SATELLITE_SOURCE_ID,
  satelliteAttributionHtml,
  satelliteProbeTileUrl,
  satelliteRasterTiles,
  VWORLD_KOREA_BOUNDS,
  VWORLD_LAYER_ID,
  VWORLD_SOURCE_ID,
  vworldRasterTiles,
  type BasemapLayerRef,
  type BasemapMode,
  VECTOR_STYLE_URL,
  vectorAttributionHtml,
  writeBasemapPref,
} from "../shared/basemap.ts";
import { trackBasemapKeyboard } from "./basemap-keyboard.ts";
import { t } from "./i18n/index.ts";
import { mapHasLayer, mapStyleReady } from "../shared/map-style-safe.ts";

const BASEMAP_KEY_HINT = "V";

export const MAP_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M3 6.5 9 4.2v13.1L3 19.5V6.5Zm7.2-2.3 6.8 2.3v13.1l-6.8-2.3V4.2ZM21 8.8v10.7l-4-1.4V7.4l4 1.4Z" fill="currentColor"/></svg>';
export const SATELLITE_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"><path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v3.8l-8 3.2-8-3.2V5.5ZM12 14.2l8-3.2v6.5A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-6.5l8 3.2Z" fill="currentColor"/></svg>';

let maptilerKey: string | null = null;
let vworldKey: string | null = null;
let keyChecked = false;
let keyUsable = false;
let vworldDisabled = false;

export function setMaptilerKey(key: string | null | undefined): void {
  const trimmed = typeof key === "string" ? key.trim() : "";
  maptilerKey = trimmed || null;
  keyChecked = false;
  keyUsable = false;
}

export function setVworldKey(key: string | null | undefined): void {
  const trimmed = typeof key === "string" ? key.trim() : "";
  vworldKey = trimmed || null;
  vworldDisabled = false;
}

export function getVworldKey(): string | null {
  return vworldKey;
}

export function getMaptilerKey(): string | null {
  return maptilerKey;
}

/** Browser keys are origin-restricted; a raster <img> load is the reliable probe (HEAD often 403). */
async function probeMaptilerKey(): Promise<boolean> {
  if (!maptilerKey) return false;
  if (keyChecked) return keyUsable;
  keyChecked = true;
  if (typeof Image === "undefined") {
    keyUsable = true;
    return keyUsable;
  }
  keyUsable = await new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = satelliteProbeTileUrl(maptilerKey!);
  });
  return keyUsable;
}

export interface MapBasemapOptions {
  map: MapLibreMap;
  /** Custom credit line (quest route map). MapLibre attribution is updated when omitted. */
  creditEl?: HTMLElement;
  /** @deprecated Style is no longer replaced; kept for callers that repainted after setStyle. */
  onStyleReady: () => void;
  /** Vector/satellite visibility changed; re-read basemap roads for snap when needed. */
  onBasemapChange?: () => void;
  onToast?: (message: string) => void;
}

/**
 * Satellite basemap toggle: vector (OpenFreeMap) by default; MapTiler raster layered underneath app content.
 * The button is omitted when the key is missing or the probe tile fails.
 */
export class MapBasemap {
  private map: MapLibreMap;
  private creditEl?: HTMLElement;
  private onBasemapChange?: () => void;
  private onToast?: (message: string) => void;
  private mode: BasemapMode;
  private button: HTMLButtonElement;
  private ready = false;

  constructor(opts: MapBasemapOptions) {
    this.map = opts.map;
    this.creditEl = opts.creditEl;
    this.onBasemapChange = opts.onBasemapChange;
    this.onToast = opts.onToast;
    this.mode = readBasemapPref(localStorage);
    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.className = "route-map-basemap map-basemap-toggle";
    this.button.hidden = true;
    this.button.addEventListener("click", (e) => {
      e.stopPropagation();
      void this.toggle();
    });
    this.map.on("error", (e) => this.onTileError(e));
    trackBasemapKeyboard(this);
  }

  /** True when the satellite toggle is shown and keyboard shortcuts may run. */
  canToggleFromKeyboard(): boolean {
    return this.ready && !this.button.hidden && this.button.isConnected;
  }

  toggleFromKeyboard(): Promise<void> {
    return this.toggle();
  }

  /** Keep satellite rasters under app/edit layers after new layers are added or modes change. */
  reconcileLayerStack(): void {
    if (!mapStyleReady(this.map)) return;
    try {
      if (!mapHasLayer(this.map, SATELLITE_LAYER_ID) && !mapHasLayer(this.map, VWORLD_LAYER_ID)) return;
      this.repositionSatelliteLayers();
    } catch {
      /* style swap */
    }
  }

  getToggleElement(): HTMLButtonElement {
    return this.button;
  }

  modeNow(): BasemapMode {
    return this.mode;
  }

  /** Wait for the initial style, then probe the key and restore a saved satellite preference. */
  async boot(): Promise<void> {
    await this.waitForLoad();
    await this.init();
  }

  private waitForLoad(): Promise<void> {
    if (this.map.loaded()) return Promise.resolve();
    return new Promise((resolve) => this.map.once("load", () => resolve()));
  }

  private styleLayers(): BasemapLayerRef[] {
    const layers = this.map.getStyle()?.layers ?? [];
    return layers.map((l) => ({
      id: l.id,
      type: l.type,
      source: "source" in l && typeof l.source === "string" ? l.source : undefined,
    }));
  }

  private repositionSatelliteLayers(): void {
    const beforeId = pickSatelliteLayerBeforeId(this.styleLayers());
    if (!beforeId) return;
    if (this.map.getLayer(VWORLD_LAYER_ID)) {
      this.map.moveLayer(VWORLD_LAYER_ID, beforeId);
      if (this.map.getLayer(SATELLITE_LAYER_ID)) {
        this.map.moveLayer(SATELLITE_LAYER_ID, VWORLD_LAYER_ID);
      }
      return;
    }
    if (this.map.getLayer(SATELLITE_LAYER_ID)) {
      this.map.moveLayer(SATELLITE_LAYER_ID, beforeId);
    }
  }

  /** Call after the first style load; probes the key and may switch to satellite. */
  async init(): Promise<void> {
    const ok = await probeMaptilerKey();
    if (!ok) {
      if (this.mode === "satellite") {
        this.mode = "vector";
        writeBasemapPref(localStorage, "vector");
        this.onToast?.(t("toast.basemapUnavailable"));
      }
      this.button.hidden = true;
      this.ready = true;
      return;
    }
    this.button.hidden = false;
    this.ready = true;
    this.paintButton();
    if (this.mode === "satellite") await this.applyMode("satellite", false, true);
    else this.paintAttribution();
  }

  async toggle(): Promise<void> {
    if (!this.ready || this.button.hidden) return;
    const next: BasemapMode = this.mode === "vector" ? "satellite" : "vector";
    await this.applyMode(next, true);
  }

  private satelliteLayerVisible(): boolean {
    const layer = this.map.getLayer(SATELLITE_LAYER_ID);
    if (!layer) return false;
    const vis = this.map.getLayoutProperty(SATELLITE_LAYER_ID, "visibility");
    return vis !== "none";
  }

  private ensureSatelliteLayer(): void {
    if (!maptilerKey) return;
    if (!this.map.getSource(SATELLITE_SOURCE_ID)) {
      this.map.addSource(SATELLITE_SOURCE_ID, {
        type: "raster",
        tiles: satelliteRasterTiles(maptilerKey),
        tileSize: 256,
        maxzoom: 20,
      });
    }
    if (!this.map.getLayer(SATELLITE_LAYER_ID)) {
      const beforeId = pickSatelliteLayerBeforeId(this.styleLayers());
      this.map.addLayer(
        {
          id: SATELLITE_LAYER_ID,
          type: "raster",
          source: SATELLITE_SOURCE_ID,
          layout: { visibility: "visible" },
        },
        beforeId,
      );
    }
    this.ensureVworldLayer();
    this.repositionSatelliteLayers();
  }

  private ensureVworldLayer(): void {
    if (!vworldKey || vworldDisabled) return;
    if (!this.map.getSource(VWORLD_SOURCE_ID)) {
      this.map.addSource(VWORLD_SOURCE_ID, {
        type: "raster",
        tiles: vworldRasterTiles(vworldKey),
        tileSize: 256,
        bounds: VWORLD_KOREA_BOUNDS,
        minzoom: 6,
        maxzoom: 19,
      });
    }
    if (!this.map.getLayer(VWORLD_LAYER_ID)) {
      const beforeId = pickSatelliteLayerBeforeId(this.styleLayers());
      this.map.addLayer(
        {
          id: VWORLD_LAYER_ID,
          type: "raster",
          source: VWORLD_SOURCE_ID,
          layout: { visibility: "visible" },
        },
        beforeId,
      );
    }
  }

  private hideVworldLayer(): void {
    vworldDisabled = true;
    if (this.map.getLayer(VWORLD_LAYER_ID)) {
      this.map.setLayoutProperty(VWORLD_LAYER_ID, "visibility", "none");
    }
  }

  private setVectorBaseVisible(visible: boolean): void {
    const show = visible ? "visible" : "none";
    for (const layer of this.styleLayers()) {
      if (!isOpenFreeMapBasemapLayer(layer)) continue;
      if (!this.map.getLayer(layer.id)) continue;
      this.map.setLayoutProperty(layer.id, "visibility", show);
    }
  }

  private async applyMode(mode: BasemapMode, persist: boolean, force = false): Promise<void> {
    if (!maptilerKey) return;
    const onSatellite = this.satelliteLayerVisible();
    if (
      !force &&
      mode === this.mode &&
      ((mode === "satellite" && onSatellite) || (mode === "vector" && !onSatellite))
    ) {
      return;
    }
    if (mode === "satellite") {
      this.ensureSatelliteLayer();
      this.map.setLayoutProperty(SATELLITE_LAYER_ID, "visibility", "visible");
      if (this.map.getLayer(VWORLD_LAYER_ID) && !vworldDisabled) {
        this.map.setLayoutProperty(VWORLD_LAYER_ID, "visibility", "visible");
      }
      this.setVectorBaseVisible(false);
    } else {
      if (this.map.getLayer(SATELLITE_LAYER_ID)) {
        this.map.setLayoutProperty(SATELLITE_LAYER_ID, "visibility", "none");
      }
      if (this.map.getLayer(VWORLD_LAYER_ID)) {
        this.map.setLayoutProperty(VWORLD_LAYER_ID, "visibility", "none");
      }
      this.setVectorBaseVisible(true);
    }
    this.mode = mode;
    if (persist) writeBasemapPref(localStorage, mode);
    this.paintButton();
    this.paintAttribution();
    this.onBasemapChange?.();
  }

  private onTileError(event: { error?: { message?: string } }): void {
    if (this.mode !== "satellite") return;
    const msg = event.error?.message ?? "";
    if (/vworld/i.test(msg)) {
      this.hideVworldLayer();
      this.paintAttribution();
      return;
    }
    if (!/maptiler|satellite|tile/i.test(msg)) return;
    void this.fallbackFromSatellite();
  }

  private async fallbackFromSatellite(): Promise<void> {
    this.mode = "vector";
    writeBasemapPref(localStorage, "vector");
    this.onToast?.(t("toast.basemapUnavailable"));
    this.button.hidden = true;
    if (this.map.getLayer(SATELLITE_LAYER_ID)) {
      this.map.setLayoutProperty(SATELLITE_LAYER_ID, "visibility", "none");
    }
    if (this.map.getLayer(VWORLD_LAYER_ID)) {
      this.map.setLayoutProperty(VWORLD_LAYER_ID, "visibility", "none");
    }
    this.setVectorBaseVisible(true);
    this.paintButton();
    this.paintAttribution();
  }

  private paintButton(): void {
    const satellite = this.mode === "vector";
    this.button.innerHTML = satellite ? SATELLITE_ICON_SVG : MAP_ICON_SVG;
    const label = t(satellite ? "rt.map.satellite" : "rt.map.map");
    const withHint = `${label} (${BASEMAP_KEY_HINT})`;
    this.button.title = withHint;
    this.button.setAttribute("aria-label", withHint);
    this.button.setAttribute("aria-keyshortcuts", BASEMAP_KEY_HINT);
    this.button.setAttribute("aria-pressed", satellite ? "false" : "true");
  }

  private paintAttribution(): void {
    const html =
      this.mode === "satellite"
        ? satelliteAttributionHtml({
            vworld: Boolean(vworldKey) && !vworldDisabled,
          })
        : vectorAttributionHtml();
    if (this.creditEl) this.creditEl.innerHTML = html;
    else {
      const el = this.map.getContainer().querySelector(".maplibregl-ctrl-attrib-inner");
      if (el) el.innerHTML = html;
    }
  }
}

export { BASEMAP_PREF_KEY, VECTOR_STYLE_URL };
